/// <amd-module name="Release/Landing/HubContent/Deployments/Store" />
import { StoreBase } from "DistributedTask/Flux/Store";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { DeploymentsActions, IDeploymentsPayload, IUpdateDeploymentStatus, IUpdateDeploymentsForCreatedReleasePayload, IDeploymentStartedPayload } from "./Actions";
import { Deployment, DefinitionEnvironmentReference, ReleaseDefinitionShallowReference, ReleaseReference, DeploymentAttempt, ReleaseEnvironment, Release, DeploymentStatus, DeploymentOperationStatus } from "Release/Client/Contracts/Release";
import { IDeploymentsFilterState } from "Release/Landing/HubContent/Filter/FilterTypes";
import { getPrimaryArtifactBranchName } from "Release/Landing/Artifact";

export interface IDeploymentsState {
    deployments: Deployment[];
    continuationToken: number;
    definitionId: number;
    filterState?: IDeploymentsFilterState
    environments: DefinitionEnvironmentReference[];
    isLoading: boolean;
}

export class DeploymentsStore extends StoreBase {
    public static getKey(): string {
        return "release-landing-deployments-store";
    }

    public initialize(instanceId?: string): void {
        super.initialize(instanceId);
        this._state = {
            isLoading: true,
            continuationToken: 0,
            definitionId: -1,
            deployments: [],
            environments: []
        };
        this._actions = ActionsHubManager.GetActionsHub<DeploymentsActions>(DeploymentsActions);
        this._actions.setDeployments.addListener(this._setDeployments);
        this._actions.deploymentsUpdatedWithMore.addListener(this._appendDeployments);
        this._actions.updateDeploymentStatus.addListener(this._updateDeploymentStatus);
        this._actions.updateDeploymentsForCreatedRelease.addListener(this._handleCreatedRelease);
        this._actions.handleDeploymentStarted.addListener(this._handleDeploymentStarted);
    }

    public getState(): IDeploymentsState {
        return this._state;
    }

    protected disposeInternal(): void {
        this._actions.setDeployments.removeListener(this._setDeployments);
        this._actions.deploymentsUpdatedWithMore.removeListener(this._appendDeployments);
        this._actions.updateDeploymentStatus.removeListener(this._updateDeploymentStatus);
        this._actions.updateDeploymentsForCreatedRelease.removeListener(this._handleCreatedRelease);
        this._actions.handleDeploymentStarted.removeListener(this._handleDeploymentStarted);
    }

    private _setDeployments = (payload: IDeploymentsPayload) => {
        this._state.continuationToken = payload.continuationToken;
        this._state.definitionId = payload.definitionId;
        this._state.deployments = payload.deployments;
        this._state.environments = payload.environments || [];
        this._state.isLoading = false;
        this._state.filterState = payload.filterState;

        this.emitChanged();
    }

    private _appendDeployments = (payload: IDeploymentsPayload): void => {
        this._state.deployments.push(...(payload.deployments || []));
        this._state.continuationToken = payload.continuationToken;
        this._state.definitionId = payload.definitionId;
        this._state.isLoading = false;

        this.emitChanged();
    }

    private _updateDeploymentStatus = (payload: IUpdateDeploymentStatus): void => {
        if (this._state.definitionId === payload.definitionId && this._state.definitionId > 0) {
            // Find all deployments for the given release environment id
            const relevantDeployments = this._state.deployments.filter(dep => dep.releaseEnvironment.id === payload.environmentId);

            // Find the latest attempt among these, as live updates will always come for the latest attempt
            const maxAttempt = Math.max(...relevantDeployments.map(dep => dep.attempt));

            // Find the deployment with this attempt and the given environment id to update the status properties
            const deployment = this._state.deployments.find(dep => dep.releaseEnvironment.id === payload.environmentId && dep.attempt === maxAttempt);
            if (deployment) {

                // A deployment shouldn't go from a completion state to in progress. The only case where equality is valid is when we get a post deploy approval. Check for operation status for that case
                if (payload.operationStatus !== DeploymentOperationStatus.Pending && payload.deploymentsStatus <= DeploymentStatus.InProgress && deployment.deploymentStatus >= payload.deploymentsStatus) {
                    return;
                }

                if (payload.deploymentsStatus === DeploymentStatus.InProgress) {
                    // This won't be the exact time, but a close value to the start time
                    // We don't have this data in case of our signalR event
                    deployment.startedOn = new Date();
                }

                deployment.deploymentStatus = payload.deploymentsStatus;
                deployment.operationStatus = payload.operationStatus;
                this.emitChanged();
            }
        }
    }

    private _handleCreatedRelease = (payload: IUpdateDeploymentsForCreatedReleasePayload): void => {
        if (this._state.definitionId === payload.definitionId && this._state.definitionId > 0) {
            const release = payload.release;
            const newDeployments: Deployment[] = [];
            release.environments.forEach(env => {
                if (env.deploySteps && env.deploySteps.length) {
                    env.deploySteps.forEach(dep => newDeployments.push(this._getDeploymentFromDeploymentAttempt(dep, env, release)));
                }
            });

            const filteredDeployments = newDeployments.filter(dep => this._deploymentMatchesFilters(dep));

            this._state.deployments.unshift(...filteredDeployments);
            this.emitChanged();
        }
    }

    private _handleDeploymentStarted = (payload: IDeploymentStartedPayload): void => {
        if (payload.definitionId === this._state.definitionId && this._state.definitionId > 0) {
            const release = payload.release;
            const environment = release.environments.find(env => env.id === payload.releaseEnvironmentId);
            if (environment) {
                const latestAttempt = Math.max(...environment.deploySteps.map(d => d.attempt));

                // Find whether the latest deployment is already added to the list. If yes, continue, else add it to the list, because
                // it is a new entry
                const existingDeployment = this._state.deployments.some(d => d.releaseEnvironment.id === payload.releaseEnvironmentId && d.attempt === latestAttempt)

                if (!existingDeployment) {
                    const deploymentAttempt = environment.deploySteps.find(d => d.attempt == latestAttempt);

                    if (deploymentAttempt) {
                        const deployment = this._getDeploymentFromDeploymentAttempt(deploymentAttempt, environment, release);
                        if (this._deploymentMatchesFilters(deployment)) {
                            this._state.deployments.unshift(deployment);
                            this.emitChanged();
                        }
                    }
                }
            }
        }
    }

    private _getDeploymentFromDeploymentAttempt(deploymentAttempt: DeploymentAttempt, environment: ReleaseEnvironment, release: Release): Deployment {
        return {
            attempt: deploymentAttempt.attempt,
            id: deploymentAttempt.deploymentId,
            deploymentStatus: deploymentAttempt.status,
            operationStatus: deploymentAttempt.operationStatus,
            releaseDefinition: environment.releaseDefinition,
            definitionEnvironmentId: environment.definitionEnvironmentId,
            release: { artifacts: release.artifacts, createdBy: release.createdBy, id: release.id, name: release.name, reason: release.reason } as ReleaseReference,
            reason: deploymentAttempt.reason,
            queuedOn: deploymentAttempt.queuedOn,
            lastModifiedBy: deploymentAttempt.lastModifiedBy,
            lastModifiedOn: deploymentAttempt.lastModifiedOn,
            releaseEnvironment: { id: environment.id, name: environment.name, url: "", _links: {} },
            requestedBy: deploymentAttempt.requestedBy,
            requestedFor: deploymentAttempt.requestedFor,
            startedOn: deploymentAttempt.lastModifiedOn // This should be the started time
        } as Deployment;
    }

    private _deploymentMatchesFilters(deployment: Deployment): boolean {
        const filterState = this._state.filterState;
        if (filterState) {
            if (filterState.branch) {
                const branchName = getPrimaryArtifactBranchName(deployment.release.artifacts);
                if (branchName !== filterState.branch) {
                    return false;
                }
            }

            if (filterState.environmentId && deployment.definitionEnvironmentId !== filterState.environmentId) {
                return false;
            }

            if (filterState.createdBy && filterState.createdBy !== deployment.requestedFor.id) {
                return false;
            }
        }

        return true;
    }

    private _state: IDeploymentsState;
    private _actions: DeploymentsActions;
}
